import React from 'react';
import { TFunction } from 'i18next';
import { upperFirst } from 'lodash';
import i18nIsoCountries from 'i18n-iso-countries';
import i18nIsoLangs from '@cospired/i18n-iso-languages';
import i18n from 'i18next';

import LanguageSelectChipContent from 'components/ui/Chip/LanguageSelectChipContent';
import { LanguageLevelInt } from 'constants/languageLevels';
import { backendNonIsoLangsMap, hiddenCountriesMap, isoMissingLangsMap } from 'constants/hiddenCountriesLanguages';

export interface OptionsItem<TValue = string | number> {
    label: string | number;
    value: TValue;
}

export interface GroupedOptionItem<TValue = string | number> {
    label: string;
    options: OptionsItem<TValue>[];
}

export const getLanguagesOptions = (): OptionsItem[] => {
    const localizedLanguages = i18nIsoLangs.getNames(i18n.language);

    const languagesOptions = Object.keys(localizedLanguages)
        .map((key) => ({
            label: localizedLanguages[key].charAt(0).toUpperCase() + localizedLanguages[key].slice(1),
            value: key.toUpperCase(),
        }))
        .filter((item) => !isoMissingLangsMap[item.value]);

    const backendNonIsoLangsOptions = Object.keys(backendNonIsoLangsMap).map((key) => ({
        label: backendNonIsoLangsMap[key],
        value: key.toUpperCase(),
    }));

    // Combine and sort the arrays by label
    return [...languagesOptions, ...backendNonIsoLangsOptions].sort((a, b) => {
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }

        return 0;
    });
};

// Former enLanguagesMap
export const getLanguagesMap = (): { [key: string]: string } => {
    const langs = i18nIsoLangs.getNames(i18n.language);

    return Object.assign(
        Object.fromEntries(
            Object.entries(langs).map(([key, value]) => [
                key.toUpperCase(),
                value.charAt(0).toUpperCase() + value.slice(1),
            ]),
        ),
        backendNonIsoLangsMap,
    );
};

export const getCountriesOptions = (): OptionsItem[] => {
    const localizedCountries = i18nIsoCountries.getNames(i18n.language, { select: 'alias' });

    return Object.keys(localizedCountries)
        .map((key) => ({
            label: localizedCountries[key],
            value: key,
        }))
        .filter((item) => !hiddenCountriesMap[item.value]);
};

// Map of country codes to country names, e.g. {US: 'United States', ...}
export const getCountriesMap = (): { [key: string]: string } => {
    const localizedCountries = i18nIsoCountries.getNames(i18n.language, { select: 'alias' });

    return Object.fromEntries(Object.entries(localizedCountries).map(([key, value]) => [key, value]));
};

export const ageOptions: OptionsItem[] = (() => {
    const ageArray = Array.from({ length: 66 }, (_, i: number) => ({
        label: String(i),
        value: String(i),
    }));

    ageArray[ageArray.length - 1].label = '65+';

    return ageArray;
})();

export const getSexOptions = (t: TFunction): OptionsItem[] => {
    return [
        { label: t('Common.Any'), value: 'everyone' },
        { label: t('UserProfile.Basic.Gender.Male'), value: 'male' },
        { label: t('UserProfile.Basic.Gender.Female'), value: 'female' },
    ];
};

interface ContinentMap {
    [key: string]: string;
}

export const getContinentMap = (t: TFunction): ContinentMap => {
    return {
        AF: t('Options.Continents.AF'),
        AS: t('Options.Continents.AS'),
        EU: t('Options.Continents.EU'),
        NA: t('Options.Continents.NA'),
        SA: t('Options.Continents.SA'),
        OC: t('Options.Continents.OC'),
    };
};

export const getContinentOptionsSorted = (t: TFunction): OptionsItem[] => {
    const continents = getContinentMap(t);

    return Object.keys(continents)
        .map((code) => ({
            label: continents[code],
            value: code,
        }))
        .sort((a, b) => Number(a.label > b.label));
};

export const getGoalsOptions = (t: TFunction): OptionsItem[] => {
    return [
        {
            label: t('Options.Goals.Meeting'),
            value: 'lfor_meet',
        },
        {
            label: t('Options.Goals.Friends'),
            value: 'lfor_friend',
        },
        {
            label: t('Options.Goals.LanguagePractice'),
            value: 'lfor_langex',
        },
        {
            label: t('Options.Goals.Relations'),
            value: 'lfor_relation',
        },
        {
            label: t('Options.Goals.Snail'),
            value: 'lfor_snail',
        },
    ];
};

export const getGoalsMatchSettingsOptions = (t: TFunction): OptionsItem[] => {
    return [
        {
            label: t('Options.Goals.Meeting'),
            value: 'meet',
        },
        {
            label: t('Options.Goals.Friends'),
            value: 'friend',
        },
        {
            label: t('Options.Goals.LanguagePractice'),
            value: 'langex',
        },
        {
            label: t('Options.Goals.Relations'),
            value: 'relation',
        },
        {
            label: t('Options.Goals.Snail'),
            value: 'snail',
        },
    ];
};

export const getSortByOptions = (t: TFunction): OptionsItem[] => {
    return [
        {
            label: t('Options.SortBy.Modified'),
            value: 'modified',
        },
        {
            label: t('Options.SortBy.LastLogin'),
            value: 'last_login',
        },
        {
            label: t('Options.SortBy.SignUp'),
            value: 'signup',
        },
        {
            label: t('Options.SortBy.Distance'),
            value: 'distance',
        },
    ];
};

export const languagesWithLevelsOptions = (): {
    label: JSX.Element;
    value: string;
}[] => {
    const langs = getLanguagesOptions();

    return langs
        .map((option: OptionsItem) =>
            Array.from({ length: 5 }, (item, index) => ({
                label: (
                    <LanguageSelectChipContent languageName={String(option.label)} level={index as LanguageLevelInt} />
                ),
                value: `${option.value}${index}`,
            })),
        )
        .flat();
};

export const educationStatuses: { [key: string]: string } = {
    edu_bachelors: 'UserProfile.Basic.EducationLevel.BachelorDegree',
    edu_college: 'UserProfile.Basic.EducationLevel.College',
    edu_graduate: 'UserProfile.Basic.EducationLevel.GraduateDegree',
    edu_high_school: 'UserProfile.Basic.EducationLevel.HighSchool',
};

export const relationshipStatuses: { [key: string]: string } = {
    rel_not_specified: 'UserProfile.Basic.RelationshipStatus.NotSpecified',
    rel_complicated: 'UserProfile.Basic.RelationshipStatus.ItsComplicated',
    rel_married: 'UserProfile.Basic.RelationshipStatus.Married',
    rel_relationship: 'UserProfile.Basic.RelationshipStatus.InRelationship',
    rel_single: 'UserProfile.Basic.RelationshipStatus.Single',
};

export const groupCategories = [
    'advice',
    'books',
    'education',
    'food',
    'fun',
    'history',
    'hobbies',
    'interpals',
    'miscellaneous',
    'moderation',
    'movies',
    'music',
    'nature',
    'news',
    'regional',
    'religion',
    'science',
    'society',
    'sports',
    'travel',
];

export const getCategoryOptions = (t: TFunction, isModerator: boolean): OptionsItem[] => {
    return groupCategories
        .map((category) => {
            return {
                label: t(`Group.Categories.${upperFirst(category)}`),
                value: category,
            };
        })
        .filter((item) => isModerator || item.value !== 'moderation')
        .sort((a, b) => Number(a.label > b.label));
};

export const getMonthOptions = (t: TFunction) => {
    return [
        { label: t('Common.DateTime.Months.January'), value: 1 },
        { label: t('Common.DateTime.Months.February'), value: 2 },
        { label: t('Common.DateTime.Months.March'), value: 3 },
        { label: t('Common.DateTime.Months.April'), value: 4 },
        { label: t('Common.DateTime.Months.May'), value: 5 },
        { label: t('Common.DateTime.Months.June'), value: 6 },
        { label: t('Common.DateTime.Months.July'), value: 7 },
        { label: t('Common.DateTime.Months.August'), value: 8 },
        { label: t('Common.DateTime.Months.September'), value: 9 },
        { label: t('Common.DateTime.Months.October'), value: 10 },
        { label: t('Common.DateTime.Months.November'), value: 11 },
        { label: t('Common.DateTime.Months.December'), value: 12 },
    ];
};
